import { graphQLClient } from '@api/client';
import { useUpdateTreatmentEndCallMutation, useVideoCallQuery } from '@api/GoodocAPI';
import { useModalContext } from '@context/Modal';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { END_CALL_KEY, START_TREATMENT_BUTTON } from '@pages/VideoCall/focuskey';
import constate from 'constate';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserMedia } from '@opentok/client';
import { END_INSTRUCTION } from '@router/path';

interface VideoCallProps {
  treatmentId: string;
}

interface PublishType {
  publishVideo: boolean;
  publishAudio: boolean;
  width: number | string;
  height: number | string;
  showControls: boolean;
  videoSource?: string | null;
  audioSource?: string | null;
}

type Connection = 'connected' | 'disconnected';

const useVideoCallStore = (props: VideoCallProps) => {
  const { treatmentId } = props;

  const { mutateAsync: updateTreatmentEndCall } =
    useUpdateTreatmentEndCallMutation(graphQLClient);

  const { visible, modalInfo, openModal, closeModal } = useModalContext();
  const navigate = useNavigate();

  const openTreatmentEndModal = () => {
    openModal({
      title: '굿닥 비대면 진료를 종료할까요?',
      initialFocusKey: START_TREATMENT_BUTTON,
      exitFocusKey: END_CALL_KEY,
      buttons: [
        {
          text: '취소',
          onClick: () => {
            closeModal();
          },
          iconType: 'cancel'
        },
        {
          text: '종료',
          onClick: () => {
            updateTreatmentEndCall({ treatmentId });
            navigate(END_INSTRUCTION, { replace: true });
            closeModal();
          },
          focusKey: START_TREATMENT_BUTTON,
          iconType: 'arrow'
        }
      ]
    });
  };

  const { data: videoCallResponse } = useVideoCallQuery(
    graphQLClient,
    { treatmentId },
    { refetchOnWindowFocus: false }
  );

  const [audioState, setAudioState] = useState(true);
  const [subscriberConnected, setSubscriberConnected] = useState(false);
  const [, setNetworkState] = useState<Connection>('connected');
  const [publishProps, setPublishProps] = useState<PublishType>();

  const setNetworkStateConnected = () => setNetworkState('connected');
  const setNetworkStateDisconnected = () => setNetworkState('disconnected');

  const { setFocus } = useFocusable();

  const setButtonFocus = useCallback(() => {
    if (visible) {
      setFocus(modalInfo.initialFocusKey);
    } else {
      setFocus(END_CALL_KEY);
    }
  }, [modalInfo.initialFocusKey, setFocus, visible]);

  // https://goodoc.atlassian.net/browse/US-413
  const initMediaSource = useCallback(async () => {
    const result = await getUserMedia()
      .then((stream) => {
        let videoSource = null;
        let audioSource = null;
        let publishVideo = false;
        let publishAudio = false;

        if (stream.getVideoTracks().length > 0) {
          videoSource = stream.getVideoTracks()[0].id;
          publishVideo = true;
        }

        if (stream.getAudioTracks().length > 0) {
          audioSource = stream.getAudioTracks()[0].id;
          publishAudio = true;
        }

        return {
          publishVideo: publishVideo,
          publishAudio: publishAudio && audioState,
          width: '20vh',
          height: '30vh',
          showControls: false,
          ...(videoSource ? undefined : { videoSource }),
          ...(audioSource ? undefined : { audioSource })
        };
      })
      .catch((reason) => {
        return {
          publishVideo: false,
          publishAudio: true,
          width: '20vh',
          height: '30vh',
          showControls: false,
          videoSource: null
        };
      });

    setPublishProps(result);
  }, [audioState]);

  return {
    setButtonFocus,
    videoCallResponse,
    subscriberConnected,
    audioState,
    publishProps,
    setSubscriberConnected,
    setNetworkStateConnected,
    setNetworkStateDisconnected,
    setAudioState,
    openTreatmentEndModal,
    setPublishProps,
    initMediaSource
  };
};

export const [VideoCallProvider, useVideoCallContext] = constate(useVideoCallStore);
